import { ReactNode } from 'react';
import { IControlProps, IPartialFormGroup } from '../types';
import type { IOption } from '../../types/IOption';
import FormControl from '../FormControl';
import Select, { ISelectProps } from '../../atoms/Select';
import { useHookFormController } from '../../hooks/useHookFormController';

export type ISelectControlProps = Omit<ISelectProps, 'onChange' | 'values'> &
    IControlProps & {
        formGroupProps?: IPartialFormGroup;
        label?: ReactNode;
        tooltip?: ReactNode;
        required?: boolean;
        onValueChange?: (value: IOption[]) => void;
        getValue?: (options: IOption[], value: any) => IOption[];
        setValue?: (value: IOption[]) => any;
    };

const SelectControl = ({
    name,
    rules,
    defaultValue = [],
    control,
    shouldUnregister,
    label,
    tooltip,
    required,
    formGroupProps,
    hideErrorMessage,
    onValueChange,
    getValue,
    setValue,
    highlightMenuItems = true,
    ...props
}: ISelectControlProps) => {
    const Controller = useHookFormController();

    const prepareValue = (options: IOption[], value: any): IOption[] | undefined => {
        if (getValue) {
            return getValue(options, value);
        } else {
            return value;
        }
    };

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            shouldUnregister={shouldUnregister}
            defaultValue={defaultValue}
            render={({ field: { value, onChange, ...fieldProps } }) => (
                <FormControl
                    readonlyAppearance={props.disabled ? (props.variant === 'tag' ? 'hint' : 'field') : undefined}
                    name={name}
                    label={label}
                    tooltip={tooltip}
                    control={control}
                    required={required}
                    hideErrorMessage={hideErrorMessage}
                    {...formGroupProps}
                >
                    <Select
                        {...fieldProps}
                        {...props}
                        highlightMenuItems={highlightMenuItems}
                        values={prepareValue(props.options, value)}
                        onChange={(option: IOption[]) => {
                            if (setValue) {
                                let val = setValue(option);
                                onChange(val);
                            } else {
                                onChange(option);
                            }
                            if (onValueChange) {
                                onValueChange(option);
                            }
                        }}
                    />
                </FormControl>
            )}
        />
    );
};

export default SelectControl;
